import { AislePhotoBlock } from "../components/AislePhoto";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  getStructureAisleMetrics, listInventoryRuns, selectedPaintableRuns,
} from "../api/rest";
import { AGREEMENT_WARN_PCT } from "../app/home/aisleAttention";
import { SegmentBar } from "../app/home/WidgetCard";
import { useUIStore } from "../stores/uiStore";
import panelCss from "./Panel.module.css";
import home from "../app/home/Home.module.css";
import css from "./ZonePanel.module.css";

const pct = (v: number | null | undefined, lang: string) =>
  v == null ? null : `${v.toLocaleString(lang, { maximumFractionDigits: 1 })}`;
const int = (v: number | null | undefined, lang: string) =>
  v == null ? null : v.toLocaleString(lang);

/**
 * ADR 278: the /live zone-inspection dock. Selection-first — the dock exists
 * only while a zone is picked on the floor (ZonesLayer click), mirroring how
 * the right inspector exists only for a picked pallet or asset. No default
 * zone, no picker chips: the floor itself is the picker.
 *
 * What it shows depends on what the zone IS: a derived aisle zone carries the
 * structure node it came from (`zone.nodeId` = `AisleMetrics.id`), so it gets
 * ADR 276's aisle reading — occupancy bar, WMS agreement, FIFO age. Any other
 * zone gets its own facts (kind, speed limit); absent data renders nothing
 * rather than a wall of em dashes.
 *
 * Reads the run the SCENE paints (ADR 198 picker, ADR 190 fallback), so the
 * dock and the pallets behind it never describe two different runs.
 */
export function ZonePanel({ siteId, warehouseId }: { siteId: string; warehouseId: string }) {
  const { t, i18n } = useTranslation();
  const zone = useUIStore((s) => s.selectedZone);
  const setSelectedZone = useUIStore((s) => s.setSelectedZone);
  const runSelection = useUIStore((s) => s.runSelection);

  const { data: runs } = useQuery({
    queryKey: ["inventory-runs", siteId],
    queryFn: () => listInventoryRuns(siteId),
    enabled: !!siteId && !!zone,
    staleTime: 60_000,
  });
  const run = selectedPaintableRuns(runs ?? [], warehouseId, runSelection)[0];

  const { data } = useQuery({
    queryKey: ["structure-aisle-metrics", siteId, warehouseId, run?.id],
    queryFn: () => getStructureAisleMetrics(siteId, warehouseId, run!.id),
    // Only an aisle-derived zone can join the metrics; a hand-authored zone
    // must not cost the 15k-address rollup.
    enabled: !!run && !!zone?.nodeId,
    staleTime: 60_000,
  });

  if (!zone) return null;

  const aisle = zone.nodeId
    ? (data?.aisles ?? []).find((a) => a.id === zone.nodeId)
    : undefined;
  const kindLabel = t(`zone.kind.${zone.kind}`, { defaultValue: zone.kind });
  const occ = aisle?.occupancy;
  const div = aisle?.divergence;
  const aging = aisle?.aging;

  return (
    <aside className={`${panelCss.panel} ${css.dock}`} aria-label={t("live.zone.title")}>
      <div className={css.head}>
        <div className={css.kicker}>{t("live.zone.title")}</div>
        <div className={css.code}>{aisle ? `${kindLabel} ${aisle.code ?? ""}`.trim() : kindLabel}</div>
        {occ && (
          <div className={css.big}>
            {pct(occ.occupancyPct, i18n.language) ?? "—"}
            {occ.occupancyPct != null && <span className={css.unit}>%</span>}
          </div>
        )}
      </div>

      <div className={css.mid}>
        {occ && (
          <SegmentBar
            ariaLabel={t("home.occupancy.barAria")}
            segments={[
              { key: "occupied", label: t("inventory.item.counted"), value: occ.occupiedSpaces,
                token: "var(--color-status-loaded)" },
              { key: "empty", label: t("inventory.item.empty"), value: occ.emptySpaces,
                token: "var(--color-status-empty)" },
              // Not-scanned is NOT empty (ADR 276): an aisle the run never
              // walked must not read as free space.
              { key: "notScanned", label: t("inventory.item.not_scanned"), value: occ.notScannedSpaces,
                token: "var(--op-unvisited)" },
            ]}
          />
        )}
        <div className={home.aisleStats}>
          {aisle && (
            <>
              {/* ADR 292: how full the used slots are. Shown only where a load
                  height was actually measured — with the size of that sample in
                  the label, because it is a mean over a fraction of the aisle's
                  pallets, not over the aisle. ADR 293: a different label when the
                  quotient leans on a clear height the twin states rather than one
                  the kit measured. */}
              {occ?.verticalFillPct != null && (
                <div className={home.aisleStat}>
                  {t(occ.verticalDerivedSpaces > 0 ? "live.zone.verticalDerived" : "live.zone.vertical",
                     { count: occ.verticalMeasuredSpaces,
                       n: int(occ.verticalMeasuredSpaces, i18n.language) })}
                  <b>{`${occ.verticalDerivedSpaces > 0 ? "≈ " : ""}${pct(occ.verticalFillPct, i18n.language)} %`}</b>
                </div>
              )}
              {/* Slots × their fill: the share of the aisle's cubic capacity in
                  use. Derived and labelled as an estimate — it reads the measured
                  bins as representative of the ones the sensor skipped. */}
              {occ?.verticalFillPct != null && occ.occupancyPct != null && (
                <div className={home.aisleStat}>
                  {t("live.zone.volumetric")}
                  <b>{`${pct((occ.occupancyPct * occ.verticalFillPct) / 100, i18n.language)} %`}</b>
                </div>
              )}
              {/* ADR 293 (Mateus): the WMS/FIFO trio used to hold three em dashes on
                  any warehouse without a stock snapshot — the wall of "—" this
                  panel's own contract forbids. Each stat now appears only with a
                  reading behind it, and the row fills with what IS known. */}
              {div && (
                <div className={home.aisleStat}>
                  {t("home.accuracy.title")}
                  <b className={div.agreementPct < AGREEMENT_WARN_PCT ? home.warn : undefined}>
                    {`${pct(div.agreementPct, i18n.language)} %`}
                  </b>
                </div>
              )}
              {aging && (
                <>
                  <div className={home.aisleStat}>
                    {t("home.aisles.avgAge")}
                    <b>{t("home.aisles.days", { days: int(Math.round(aging.avgAgeDays), i18n.language) })}</b>
                  </div>
                  <div className={home.aisleStat}>
                    {t("home.aisles.over90")}
                    <b>{int(aging.over90Days, i18n.language)}</b>
                  </div>
                </>
              )}
            </>
          )}
          {!aisle && zone.maxSpeedMps != null && (
            <div className={home.aisleStat}>
              {t("live.zone.maxSpeed")}
              <b>{`${pct(zone.maxSpeedMps, i18n.language)} m/s`}</b>
            </div>
          )}
        </div>
      </div>

      {/* ADR 398: the aisle's picture. Bound to the aisle (its structure node), not to a
          count — so it shows with no run selected, unlike the numbers beside it. */}
      {zone.nodeId && siteId && warehouseId && (
        <AislePhotoBlock siteId={siteId} warehouseId={warehouseId} nodeId={zone.nodeId}
                         aisle={aisle?.code ?? zone.nodeId.split("/").pop()?.replace(/^aisle-/, "") ?? ""} />
      )}

      <button
        type="button"
        className={css.close}
        aria-label={t("common.close")}
        onClick={() => setSelectedZone(null)}
      >
        ×
      </button>
    </aside>
  );
}
